import { apiClient } from '../../../config/api';

const API_BASE = '/api/v1/gth-common/silver-connect';
const API_BASE_V2 = '/api/v2/gth-common/silver-connect';

// ========================================
// Type Definitions (서버 응답 타입)
// ========================================

interface ApiResponse<T> {
  result: T;
  resultType: string;
}

interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

export interface CursorPageResponse<T> {
  content: T[];
  nextCursor: string | null;
  hasNext: boolean;
  size: number;
}

export interface MessageResponse {
  id: number;
  conversationId: number;
  senderUserId: number;
  senderNickname: string;
  senderProfileImageUrl: string | null;
  content: string;
  isRead: boolean;
  createdAt: string; // ISO 8601 datetime
}

export interface ConversationResponse {
  id: number;
  cupidCardId: number;
  otherUserId: number;
  otherUserNickname: string;
  otherUserProfileImageUrl: string | null;
  lastMessage: string | null;
  lastMessageAt: string | null; // ISO 8601 datetime
  unreadCount: number;
  isExpired?: boolean; // 만료된 카드의 대화인지 여부
  isExtendable?: boolean; // 연장 가능 여부 (EXPIRED_EXTENDABLE 상태)
}

export interface ConversationDetailResponse {
  id: number;
  cupidCardId: number | null;
  otherUserId: number;
  otherUserNickname: string;
  otherUserProfileImageUrl: string | null;
  isExpired: boolean;
  isNew: boolean;
  unreadCount: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
  // 연장 관련 (서버 Task #9 완료 후 활성화)
  isExtendable?: boolean;        // 연장 가능 여부 (EXPIRED_EXTENDABLE 상태)
  extendDeadlineAt?: string;     // 연장 마감 시각 (ISO 8601)
  extendCostHearts?: number;     // 연장 비용 (기본 3)
  // 상호 큐피트카드 채팅 채널 마감 시각 (max(cardA.expiresAt, cardB.expiresAt)), ISO 8601
  chatDeadline?: string | null;
}

interface SendMessageRequest {
  cupidCardId: number;
  receiverUserId: number;
  content: string;
}

interface UnreadCountResponse {
  count: number;
}

interface MarkAsReadResponse {
  updatedCount: number;
}

// ========================================
// API Functions
// ========================================

/**
 * 쪽지 전송
 * POST /api/v1/gth-common/silver-connect/messages
 */
export async function sendMessage(
  cupidCardId: number,
  receiverUserId: number,
  content: string
): Promise<MessageResponse> {
  const response = await apiClient.post<ApiResponse<MessageResponse>>(
    `${API_BASE}/messages`,
    {
      cupidCardId,
      receiverUserId,
      content,
    } as SendMessageRequest
  );
  return response.result;
}

/**
 * 대화에 메시지 전송 (conversationId 기반, conversationDetail 없이도 가능)
 * POST /api/v1/gth-common/silver-connect/conversations/{conversationId}/messages
 */
export async function sendMessageToConversation(
  conversationId: number,
  content: string
): Promise<MessageResponse> {
  const response = await apiClient.post<ApiResponse<MessageResponse>>(
    `${API_BASE}/conversations/${conversationId}/messages`,
    { content }
  );
  return response.result;
}

/**
 * 대화 목록 조회
 * GET /api/v1/gth-common/silver-connect/conversations
 */
export async function getConversations(
  page: number = 0,
  size: number = 20
): Promise<PageResponse<ConversationResponse>> {
  const response = await apiClient.get<ApiResponse<PageResponse<ConversationResponse>>>(
    `${API_BASE}/conversations?page=${page}&size=${size}`
  );
  return response.result;
}

/**
 * 대화 상세 조회
 * GET /api/v1/gth-common/silver-connect/conversations/{conversationId}
 */
export async function getConversationDetail(
  conversationId: number
): Promise<ConversationDetailResponse> {
  const response = await apiClient.get<ApiResponse<ConversationDetailResponse>>(
    `${API_BASE}/conversations/${conversationId}`
  );
  return response.result;
}

/**
 * 대화의 메시지 목록 조회 (offset 기반 - deprecated)
 * GET /api/v1/gth-common/silver-connect/conversations/{conversationId}/messages
 */
export async function getMessages(
  conversationId: number,
  page: number = 0,
  size: number = 50
): Promise<PageResponse<MessageResponse>> {
  const response = await apiClient.get<ApiResponse<PageResponse<MessageResponse>>>(
    `${API_BASE}/conversations/${conversationId}/messages?page=${page}&size=${size}`
  );
  return response.result;
}

/**
 * 대화의 메시지 목록 조회 (커서 기반 V2)
 * GET /api/v2/gth-common/silver-connect/conversations/{conversationId}/messages?cursor={lastMsgId}&size=50
 * cursor 미지정 시 최신 메시지부터 반환 (역순), cursor 지정 시 해당 ID 이전 메시지 반환
 */
export async function getMessagesByCursor(
  conversationId: number,
  cursor?: string | null,
  size: number = 50
): Promise<CursorPageResponse<MessageResponse>> {
  const params = new URLSearchParams({ size: String(size) });
  if (cursor != null) {
    params.set('cursor', String(cursor));
  }
  const response = await apiClient.get<ApiResponse<CursorPageResponse<MessageResponse>>>(
    `${API_BASE_V2}/conversations/${conversationId}/messages?${params.toString()}`
  );
  return response.result;
}

/**
 * 새 메시지 폴링 (커서 기반 V2) - 마지막 메시지 ID 이후의 새 메시지만 조회
 * GET /api/v2/gth-common/silver-connect/conversations/{conversationId}/messages?afterCursor={lastMsgId}&size=50
 */
export async function getNewMessages(
  conversationId: number,
  afterCursor: number
): Promise<CursorPageResponse<MessageResponse>> {
  const response = await apiClient.get<ApiResponse<CursorPageResponse<MessageResponse>>>(
    `${API_BASE_V2}/conversations/${conversationId}/messages?afterCursor=${afterCursor}&size=50`
  );
  return response.result;
}

/**
 * 메시지 읽음 처리
 * PATCH /api/v1/gth-common/silver-connect/conversations/{conversationId}/read
 */
export async function markMessagesAsRead(
  conversationId: number
): Promise<number> {
  const response = await apiClient.patch<ApiResponse<MarkAsReadResponse>>(
    `${API_BASE}/conversations/${conversationId}/read`
  );
  return response.result.updatedCount;
}

/**
 * 읽지 않은 메시지 총 개수
 * GET /api/v1/gth-common/silver-connect/messages/unread-count
 */
export async function getUnreadCount(): Promise<number> {
  const response = await apiClient.get<ApiResponse<UnreadCountResponse>>(
    `${API_BASE}/messages/unread-count`
  );
  return response.result.count;
}

/**
 * 큐피트카드로 대화 조회/생성
 * GET /api/v1/gth-common/silver-connect/conversations/by-card/{cupidCardDeliveryId}
 */
export async function getConversationByCard(
  cupidCardDeliveryId: number
): Promise<ConversationDetailResponse> {
  const response = await apiClient.get<ApiResponse<ConversationDetailResponse>>(
    `${API_BASE}/conversations/by-card/${cupidCardDeliveryId}`
  );
  return response.result;
}
